/**
 * useCarousel Hook
 * Manages auto-rotating carousel logic for content cards
 */

import { useState, useEffect, useCallback } from 'react';

const useCarousel = (items, options = {}) => {
  const {
    visibleCount = 6,      // Number of items visible at once
    rotateCount = 3,       // Number of items to rotate
    interval = 8000,       // Time between rotations (ms)
    enabled = true         // Whether carousel is enabled
  } = options;

  const [visibleItems, setVisibleItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Determine if we should enable carousel (need more than visibleCount items)
  const shouldRotate = enabled && items.length >= visibleCount && visibleCount > 0;

  // Initialize visible items
  useEffect(() => {
    if (items.length === 0) return;

    if (items.length <= visibleCount) {
      // Show all items if we have fewer than visibleCount
      setVisibleItems(items);
    } else {
      // Show first visibleCount items
      setVisibleItems(items.slice(0, visibleCount));
    }
  }, [items, visibleCount]);

  // Rotate items
  const rotate = useCallback(() => {
    if (!shouldRotate || isAnimating) return;

    setIsAnimating(true);

    // Calculate next index
    const nextIndex = (currentIndex + rotateCount) % items.length;

    // Get next set of visible items
    const nextItems = [];
    for (let i = 0; i < visibleCount; i++) {
      const itemIndex = (nextIndex + i) % items.length;
      nextItems.push(items[itemIndex]);
    }

    // Update visible items after animation
    setTimeout(() => {
      setVisibleItems(nextItems);
      setCurrentIndex(nextIndex);
      setIsAnimating(false);
    }, 600); // Match animation duration

  }, [items, currentIndex, visibleCount, rotateCount, shouldRotate, isAnimating]);

  // Auto-rotate on interval
  useEffect(() => {
    if (!shouldRotate) return;

    const timer = setInterval(rotate, interval);
    return () => clearInterval(timer);
  }, [shouldRotate, interval, rotate]);

  return {
    visibleItems,
    isAnimating,
    shouldRotate,
    rotate,
    currentIndex,
    totalItems: items.length
  };
};

export default useCarousel;
